import React, { FC, ChangeEvent, useRef } from 'react';
import { DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { IDocument } from 'src/interfaces/accounts/account';
import styles from './uploader.module.less';
import { uniqueKey } from 'src/helpers/string.utils';
import { DEFAULT_ALLOWED_FILES, FILE_SIZE_ALLOWED } from 'src/constants';
import { Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { i18nKey } from 'src/locales/i18n';

export interface IFileUploader {
	typeId: string;
	inputFiles: IDocument[];
	title?: React.ReactNode;
	accept?: string[];
	disabled?: boolean;
	allowSize?: number;
	onChange?: (files: File) => void;
	onDeleted?: (type: string, id: string) => void;
	onDownload?: (type: string, id: string | undefined) => void;
	description?: string;
	requireMinimumSize?: boolean;
	showDivider?: boolean;
	requireUpload?: boolean;
}
const FileUploader: FC<IFileUploader> = ({
	disabled,
	inputFiles,
	accept = DEFAULT_ALLOWED_FILES,
	title,
	typeId,
	allowSize = FILE_SIZE_ALLOWED.SINGLE,
	onChange,
	onDeleted,
	onDownload,
	description,
	requireMinimumSize,
	showDivider = true,
	requireUpload
}) => {
	const rFile = useRef<HTMLInputElement>(null);
	const [t] = useTranslation();

	const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files || !e.target.files.length) {
			return;
		}

		const file = e.target.files[0];

		if (requireMinimumSize) {
			if (file.size < FILE_SIZE_ALLOWED.MINIMUM_SIZE_FILE) {
				message.error(t(i18nKey.feedback.minFileSizeReached));
				e.target.value = '';
				return;
			}

			if (file.size > allowSize) {
				message.error(t(i18nKey.feedback.maxFileSizeReactedBetween));
				e.target.value = '';
				return;
			}
		}

		if (file.size > allowSize) {
			message.error(t(i18nKey.feedback.maxFileSizeReached));
			e.target.value = '';
			return;
		}

		onChange && onChange(file);
		e.target.value = '';
	};

	const onUploadClick = () => {
		rFile.current && rFile.current.click();
	};

	const onFileDelete = (id: string | undefined) => {
		if (!id || !onDeleted) {
			return;
		}
		onDeleted(typeId, id);
	};

	const onDownloadClick = (id: string | undefined) => {
		onDownload && onDownload(typeId, id);
	};

	const renderFiles = () => {
		const rs = [];
		const fLen = inputFiles.length || 1;
		for (let i = 0; i < fLen; i++) {
			const data = inputFiles[i] || {};
			rs.push(
				<div key={uniqueKey(16, typeId)} className={styles.fileInputContainer}>
					<div className={styles.fileName}>{data.fileName || '-'}</div>
					{data && data.id && (
						<div
							className={styles.download}
							onClick={() => {
								onDownloadClick(data.id);
							}}>
							<DownloadOutlined
								style={{
									color: '#FFF',
									fontWeight: 800,
									fontSize: '2.3rem'
								}}
							/>
						</div>
					)}
					{!disabled && data.id && (
						<Button
							className={styles.icon}
							onClick={() => {
								onFileDelete(data.id);
							}}>
							<DeleteOutlined
								style={{
									fontSize: '2.3rem'
								}}
							/>
						</Button>
					)}
				</div>
			);
		}
		return rs;
	};

	return (
		<div className={styles.container} id={`uploader__${typeId}`}>
			<div className={styles.titleContainer}>
				<div className={styles.wrapperTitleAndDes}>
					<div className={styles.title}>
						{title}
						{requireUpload && <span className={styles.requireAsterisk}>*</span>}
					</div>
					<div className={styles.description}>{description}</div>
				</div>
				{!disabled && (
					<Button type="primary" className={styles.upload} onClick={onUploadClick}>
						<UploadOutlined
							style={{
								color: '#FFF',
								fontWeight: 800,
								fontSize: '2.3rem'
							}}
						/>
					</Button>
				)}
			</div>
			{renderFiles().map((Item) => Item)}
			{showDivider ? <div className={styles.dividerLine}></div> : <div></div>}
			<input ref={rFile} type="file" onChange={onFileChange} hidden accept={`${accept ? accept.join(',') : '*'}`} />
		</div>
	);
};

export default FileUploader;
